import axios, {
  AxiosResponse,
  AxiosError,
  AxiosInstance,
  AxiosPromise,
  InternalAxiosRequestConfig,
} from 'axios';
import Qs from 'qs';
import { objectType } from 'type';
import { openNotificationWithIcon } from './helper';

const onSuccessInterceptorRequest = async (
  config: InternalAxiosRequestConfig,
) => {
  config.paramsSerializer = (params: any) =>
    Qs.stringify(params, {
      arrayFormat: 'brackets',
    });
  return config;
};
const onErrorInterceptorRequest = (error: AxiosError) => Promise.reject(error);

const onErrorInterceptorResponse = (error: AxiosError) => {
  if (error.response && error.response.status) {
    if (error.response.status !== 200) {
      const data = error.response.data as { msg?: string };
      if (data?.msg) {
        openNotificationWithIcon('error', data.msg, undefined, 5);
      }
    }
  }
  return Promise.reject(error);
};
const onSuccessInterceptorResponse = (response: AxiosResponse) => response;

axios.defaults.headers.post['Content-Type'] = 'application/json';
axios.defaults.headers.post.Accept = 'application/json';

const _axios: AxiosInstance = axios.create({
  // Preview deployments may omit NEXT_PUBLIC_API_URL. Falling back to the
  // public Panoee API keeps SSR and browser requests from being sent to the
  // Vercel page itself (which returns an HTML 404/400 response).
  baseURL:
    process.env.NEXT_PUBLIC_API_URL || 'https://studio-api.panoee.com/api/',
  timeout: 120 * 1000,
  // withCredentials: true, // Check cross-site Access-Control
});

_axios.interceptors.request.use(
  onSuccessInterceptorRequest,
  onErrorInterceptorRequest,
);

_axios.interceptors.response.use(
  onSuccessInterceptorResponse,
  onErrorInterceptorResponse,
);

/**
 *
 * @NOTE primary methods axios
 *
 */
class AxiosXHRConstructor {
  axiosInstance: AxiosInstance;

  constructor(axiosInstance: AxiosInstance) {
    this.axiosInstance = axiosInstance;
    this.$get = this.$get.bind(this);
    this.$getWithAuth = this.$getWithAuth.bind(this);
    this.$post = this.$post.bind(this);
    this.$put = this.$put.bind(this);
    this.$delete = this.$delete.bind(this);
  }

  public $getWithAuth(url: string, params?: objectType): AxiosPromise {
    return this.axiosInstance.get(url, params);
  }

  public $get(url: string, params?: objectType): AxiosPromise {
    return this.axiosInstance.get(url, {
      params,
    });
  }

  public $post(
    url: string,
    data?: objectType,
    config?: objectType,
  ): AxiosPromise {
    return this.axiosInstance.post(url, data, config);
  }

  public $put(
    url: string,
    data?: objectType,
    config?: objectType,
  ): AxiosPromise {
    return this.axiosInstance.put(url, data, config);
  }

  public $delete(
    url: string,
    data?: objectType,
    config?: objectType,
  ): AxiosPromise {
    return this.axiosInstance.delete(url, {
      data,
      ...config,
    });
  }
}

export const BaseXHR = new AxiosXHRConstructor(_axios);

export default _axios;
